import type { RouterOutputs } from "lib/trpc";

/**
 * Thread comment type from the server API (via tRPC router outputs)
 */
export type ApiThreadComment = RouterOutputs["threads"]["getComments"][number];

/**
 * Thread comment author type from the server API
 */
export type ApiThreadCommentAuthor = ApiThreadComment["author"];

/**
 * Thread contexts supported by the unified comment system
 */
export type ThreadContext = "social" | "discussion" | "blog" | "vendor" | "multimedia";

/**
 * Configuration for comment features based on context
 */
export interface ThreadCommentFeatures {
    /** Show reactions (likes, etc.) */
    reactions?: boolean;
    /** Show attachments */
    attachments?: boolean;
    /** Enable profile links */
    profileLinks?: boolean;
    /** Show share button */
    shareButton?: boolean;
    /** Show edited indicator */
    editedIndicator?: boolean;
    /** Support anonymous posting */
    anonymous?: boolean;
    /** Support nested replies */
    replies?: boolean;
}

/**
 * Get default features for a thread context
 */
export function getContextFeatures(context: ThreadContext): ThreadCommentFeatures {
    const baseFeatures: ThreadCommentFeatures = {
        reactions: false,
        attachments: true,
        profileLinks: true,
        shareButton: false,
        editedIndicator: true,
        anonymous: false,
        replies: true,
    };

    switch (context) {
        case "social":
            return {
                ...baseFeatures,
                reactions: true,
                anonymous: true,
            };
        case "discussion":
            return {
                ...baseFeatures,
                shareButton: true,
            };
        case "blog":
            return {
                ...baseFeatures,
                anonymous: true,
            };
        case "vendor":
            return {
                ...baseFeatures,
                anonymous: true,
            };
        case "multimedia":
            return {
                ...baseFeatures,
                reactions: true,
            };
        default:
            return baseFeatures;
    }
}

/**
 * Format author display name.
 * The server now computes the correct name for each author type,
 * so we simply return author.name.
 */
export function formatAuthorName(author: ApiThreadCommentAuthor): string {
    return author.name;
}
